import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import ErrorToast from '../components/ErrorToast';
import { useAuth } from '../context/AuthContext';
import {getUserById, logout, updateUser} from '../services/user-service';
import User from '../models/User';
import SuccessToast from "../components/SuccessToast";
import DeleteUserModal from "../components/DeleteUserModal";

const ProfileSettingsPage: React.FC = ()=> {
    const { user: authUser, clearSession } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errorToast, setErrorToast] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [successToast, setSuccessToast] = useState(location.state?.message || '');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const [user, setUser] = useState<User | null>(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');

    const initial = useMemo(() => {
        const f = user?.firstName?.[0]?.toUpperCase() ?? '';
        const l = user?.lastName?.[0]?.toUpperCase() ?? '';
        const combined = `${f}${l}`;
        if (combined) return combined;
        if (user?.email) return user.email[0].toUpperCase();
        return 'U';
    }, [user]);

    const accountCreatedDisplay = useMemo(() => {
        if (!user?.created) return '';
        try {
            const date = new Date(user.created);
            return date.toLocaleDateString();
        } catch {
            return user.created;
        }
    }, [user]);

    const isDirty = useMemo(() => {
        if (!user) return false;
        return firstName !== (user.firstName || '') || lastName !== (user.lastName || '');
    }, [user, firstName, lastName]);

    const isValid = useMemo(() => {
        return firstName.trim().length > 0 && lastName.trim().length > 0;
    }, [firstName, lastName]);

    useEffect(() => {
        let mounted = true;
        async function load() {
            if (authUser?.id) {
                try {
                    const response = await getUserById(authUser.id);
                    if (!mounted) return;
                    if (response.status === 200 && response.data) {
                        const fullUser = response.data;
                        setUser(fullUser);
                        setFirstName(fullUser.firstName || '');
                        setLastName(fullUser.lastName || '');
                    } else {
                        console.error('Unable to load user', response);
                        setErrorToast('Unable to load user at this time. Please try again later.');
                    }
                } catch (e: any) {
                    console.error('Error loading user', e);
                    setErrorToast('Failed to load user profile. Please try again later.');
                } finally {
                    if (mounted) setLoading(false);
                }
            }
        }
        load();
        return () => {
            mounted = false;
        };
    }, [authUser?.id]);

    const handleCancel = () => {
        if (!user) return;
        setFirstName(user.firstName || '');
        setLastName(user.lastName || '');
    };

    const validate = (): boolean => {
        if (firstName.length > 50) {
            setErrorMessage('First name must be 50 characters or fewer');
            return false;
        } if (lastName.length > 50) {
            setErrorMessage('Last name must be 50 characters or fewer');
            return false;
        }
        return true;
    }

    const handleSave = async () => {
        if (!user || !validate()) return;
        try {
            setSaving(true);
            const payload: Partial<User> = { id: user.id, firstName: firstName.trim(), lastName: lastName.trim() } as any;
            const response = await updateUser(payload as User);
            if (response.status === 200 && response.data) {
                const updated = response.data
                setUser(updated);
                setFirstName(updated.firstName || '');
                setLastName(updated.lastName || '');
                setSuccessToast('Profile info updated!');
            } else {
                console.error('Failed to save updates', response);
                setErrorToast('Failed to save updates. Please try again later.');
            }
        } catch (e: any) {
            console.error('Failed to save updates', e);
            setErrorToast('Failed to save updates. Please try again later.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = () => {
        if(user?.email) {
            logout(user?.email)
        }
        clearSession();
        navigate("/account-deleted");
    }

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            <NavBar />

            <main className="flex-1">
                <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
                    <h1 className="text-2xl font-semibold text-gray-900">Profile Settings</h1>

                    {/* Header card */}
                    <div className="mt-6 rounded-xl bg-white shadow-sm ring-1 ring-gray-200 p-6">
                        {loading ? (
                            <p className="text-gray-500" data-testid="loading">Loading profile…</p>
                        ) : (
                            <div data-testid="user-info" className="flex items-start gap-4">
                                <div data-testid="user-initials" className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-crumbblue text-white text-xl font-semibold" aria-hidden>
                                    {initial}
                                </div>
                                <div className="flex-1">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">First name</label>
                                            <input
                                                id="firstName"
                                                data-testid="firstName"
                                                type="text"
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-crumborange focus:ring-crumborange"
                                                value={firstName}
                                                onChange={(e) => setFirstName(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">Last name</label>
                                            <input
                                                id="lastName"
                                                data-testid="lastName"
                                                type="text"
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-crumborange focus:ring-crumborange"
                                                value={lastName}
                                                onChange={(e) => setLastName(e.target.value)}
                                                required
                                            />
                                        </div>
                                        {errorMessage && <p data-testid="name-error" className="text-red-500 text-sm mt-1">{errorMessage}</p>}
                                        <div className="sm:col-span-2">
                                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email (login)</label>
                                            <input
                                                id="email"
                                                data-testid="email"
                                                type="email"
                                                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 text-gray-700 shadow-sm cursor-not-allowed"
                                                value={user?.email || ''}
                                                readOnly
                                                aria-readonly
                                            />
                                        </div>
                                        <div className="sm:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <span className="block text-sm text-gray-500">Account created</span>
                                                <span className="mt-1 block text-sm font-medium text-gray-900" data-testid="created">{accountCreatedDisplay}</span>
                                            </div>
                                            <div>
                                                <span className="block text-sm text-gray-500">Password</span>
                                                <Link
                                                    to="/change-password"
                                                    className="mt-1 inline-flex items-center text-sm font-medium text-crumborange hover:underline"
                                                    data-testid="reset-password-link"
                                                >
                                                    Change password
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="mt-6 flex items-center gap-3">
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={!isDirty || !isValid || saving || loading}
                                className="inline-flex items-center rounded-lg bg-crumborange px-4 py-2 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="save-btn"
                            >
                                {saving ? 'Saving...' : 'Save changes'}
                            </button>
                            <button
                                type="button"
                                onClick={handleCancel}
                                disabled={!isDirty || saving || loading}
                                className="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-gray-700 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="cancel-btn"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>

                    {/* Danger Zone */}
                    <div className="mt-8 rounded-xl bg-white shadow-sm ring-1 ring-red-200">
                        <div className="p-6 border-b border-red-200">
                            <h2 className="text-lg font-semibold text-red-700">Danger zone</h2>
                            <p className="text-sm text-red-600 mt-1">Deleting your account is permanent and cannot be undone.</p>
                        </div>
                        <div className="p-6 flex items-center justify-between gap-4">
                            <div>
                                <p className="text-sm text-gray-700">Delete your account</p>
                                <p className="text-xs text-gray-500">This will remove your data from CrumbCRM.</p>
                            </div>
                            <button
                                type="button"
                                className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-white font-medium"
                                data-testid="delete-account-btn"
                                onClick={() => setIsDeleteModalOpen(true)}
                            >
                                Delete account
                            </button>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />

            {user && user.id && (
                <DeleteUserModal
                    isOpen={isDeleteModalOpen}
                    userId={user.id}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onDeleted={() => handleDelete()}
                    onError={(errorMessage) => setErrorToast(errorMessage)}
                />
            )}
            <ErrorToast message={errorToast} clearError={() => setErrorToast('')} />
            <SuccessToast message={successToast} clearSuccess={() => setSuccessToast('')} />
        </div>
    );
}

export default ProfileSettingsPage;
